import React, { useState, useEffect } from 'react';
import { DownOutlined, SettingOutlined, FacebookOutlined, InstagramOutlined, GlobalOutlined, MessageOutlined, MobileOutlined, DesktopOutlined } from '@ant-design/icons';
import { Radio, Checkbox, Tag, Progress } from 'antd';
import { message } from 'antd';

const MetaPlacements = ({ campaignData, onUpdate, onNext }) => {
  const [placementType, setPlacementType] = useState(campaignData.metaAds?.placementType || 'advantage');
  const [selectedPlacements, setSelectedPlacements] = useState(campaignData.metaAds?.selectedPlacements || {
    facebook: [],
    instagram: [],
    audienceNetwork: [],
    messenger: []
  });
  const [deviceType, setDeviceType] = useState(campaignData.metaAds?.deviceType || 'all');
  const [operatingSystem, setOperatingSystem] = useState(campaignData.metaAds?.operatingSystem || 'both');

  const placementOptions = {
    facebook: [
      { value: 'feed', label: 'Feed' },
      { value: 'reels', label: 'Reels' },
      { value: 'stories', label: 'Stories' },
      { value: 'right_column', label: 'Right Column' },
      { value: 'in_stream_video', label: 'In-Stream Video' },
      { value: 'search_results', label: 'Search Results' },
    ],
    instagram: [
      { value: 'feed', label: 'Feed' },
      { value: 'reels', label: 'Reels' },
      { value: 'stories', label: 'Stories' },
      { value: 'explore', label: 'Explore' },
    ],
    audienceNetwork: [
      { value: 'native', label: 'Native' },
      { value: 'banner', label: 'Banner' },
      { value: 'interstitial', label: 'Interstitial' },
    ],
    messenger: [
      { value: 'inbox', label: 'Inbox' },
      { value: 'stories', label: 'Stories' },
    ],
  };

  // Calculate estimated reach impact based on selected placements
  const calculateReachImpact = () => {
    let baseReach = 1000000;
    let multiplier = 1;

    // Facebook placements
    if (selectedPlacements.facebook.length > 0) {
      multiplier += selectedPlacements.facebook.length * 0.2;
    }

    // Instagram placements
    if (selectedPlacements.instagram.length > 0) {
      multiplier += selectedPlacements.instagram.length * 0.15;
    }

    // Audience Network
    if (selectedPlacements.audienceNetwork.length > 0) {
      multiplier += selectedPlacements.audienceNetwork.length * 0.1;
    }

    // Messenger
    if (selectedPlacements.messenger.length > 0) {
      multiplier += selectedPlacements.messenger.length * 0.05;
    }

    // Device type adjustments
    if (deviceType === 'mobile_only') {
      multiplier *= 1.3; // Mobile has higher reach
    } else if (deviceType === 'desktop_only') {
      multiplier *= 0.7; // Desktop has lower reach
    }

    return Math.round(baseReach * multiplier);
  };

  const estimatedReach = calculateReachImpact();

  // Get selected platforms summary
  const getSelectedPlatforms = () => {
    const platforms = [];
    if (selectedPlacements.facebook.length > 0) platforms.push('Facebook');
    if (selectedPlacements.instagram.length > 0) platforms.push('Instagram');
    if (selectedPlacements.audienceNetwork.length > 0) platforms.push('Audience Network');
    if (selectedPlacements.messenger.length > 0) platforms.push('Messenger');
    return platforms;
  };

  const selectedPlatforms = getSelectedPlatforms();

  const handlePlacementChange = (platform, values) => {
    setSelectedPlacements({
      ...selectedPlacements,
      [platform]: values
    });
  };

  // Keep parent data in sync as fields change (single-page campaign view)
  useEffect(() => {
    onUpdate({
      metaAds: {
        placementType,
        selectedPlacements,
        deviceType,
        operatingSystem,
      }
    });
  }, [placementType, selectedPlacements, deviceType, operatingSystem]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    if (placementType === 'manual' && selectedPlatforms.length === 0) {
      message.warning('Please select at least one platform for manual placements');
      return;
    }

    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        placementType,
        selectedPlacements,
        deviceType,
        operatingSystem,
      }
    });
    onNext();
  };

  const renderPlatformSection = (platform, title, icon, options) => (
    <div style={{
      background: '#fff',
      border: '1px solid var(--paper-300)',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        {icon}
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{title}</span>
        <Tag color="blue" size="small">{selectedPlacements[platform]?.length || 0} selected</Tag>
      </div>
      <Checkbox.Group
        options={options}
        value={selectedPlacements[platform]}
        onChange={(values) => handlePlacementChange(platform, values)}
      />
    </div>
  );

  return (
    <div className="step-container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px 100px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <SettingOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#000',
          marginBottom: '12px',
          textAlign: 'center'
        }}>
          Placements
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--ink-200)',
          marginBottom: 0,
          textAlign: 'center'
        }}>
          Choose where your ads will appear
        </p>
      </div>

      {/* Placements Summary Panel */}
      {placementType === 'manual' && (
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <GlobalOutlined style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                Estimated Daily Reach: {estimatedReach.toLocaleString()}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
                Selected Platforms: {selectedPlatforms.join(', ') || 'None'}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Reach Impact</div>
            <Progress
              percent={Math.min((estimatedReach / 2000000) * 100, 100)}
              showInfo={false}
              strokeColor="var(--ink)"
              size="small"
              style={{ width: '120px' }}
            />
          </div>
        </div>
      )}

      {/* Placement Type Selection */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
          Placement Type
        </h3>

        <Radio.Group value={placementType} onChange={(e) => setPlacementType(e.target.value)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '16px',
              background: placementType === 'advantage' ? '#f0f9ff' : '#fff',
              border: placementType === 'advantage' ? '2px solid var(--ink)' : '1px solid var(--paper-300)',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
            onClick={() => setPlacementType('advantage')}
            >
              <Radio value="advantage" style={{ marginTop: '2px' }} />
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                  Advantage+ Placements (Recommended)
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
                  Meta automatically optimizes where your ads appear across all available placements for maximum performance
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '16px',
              background: placementType === 'manual' ? '#f0f9ff' : '#fff',
              border: placementType === 'manual' ? '2px solid var(--ink)' : '1px solid var(--paper-300)',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
            onClick={() => setPlacementType('manual')}
            >
              <Radio value="manual" style={{ marginTop: '2px' }} />
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                  Manual Placements
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
                  Choose specific placements across Facebook, Instagram, Audience Network, and Messenger
                </div>
              </div>
            </div>
          </div>
        </Radio.Group>
      </div>

      {/* Manual Placement Options */}
      {placementType === 'manual' && (
        <>
          {/* Platform Placements + Device & OS side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px', alignItems: 'start' }}>

          {/* Platform Placements */}
          <div style={{ background: '#fff', border: '1px solid var(--paper-300)', borderRadius: '12px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
              Platform Placements
            </h3>

            {renderPlatformSection('facebook', 'Facebook', <FacebookOutlined style={{ color: '#1877f2' }} />, placementOptions.facebook)}
            {renderPlatformSection('instagram', 'Instagram', <InstagramOutlined style={{ color: '#e4405f' }} />, placementOptions.instagram)}
            {renderPlatformSection('audienceNetwork', 'Audience Network', <GlobalOutlined style={{ color: 'var(--ink-200)' }} />, placementOptions.audienceNetwork)}
            {renderPlatformSection('messenger', 'Messenger', <MessageOutlined style={{ color: '#0084ff' }} />, placementOptions.messenger)}
          </div>

          {/* Device & OS Settings */}
          <div style={{
            background: '#fff',
            border: '1px solid var(--paper-300)',
            borderRadius: '12px',
            padding: '24px',
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
              Device & Operating System
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                Device Type
              </label>
              <Radio.Group value={deviceType} onChange={(e) => setDeviceType(e.target.value)}>
                <Radio value="all">All</Radio>
                <Radio value="mobile_only">
                  <MobileOutlined style={{ marginRight: '4px' }} />
                  Mobile Only
                </Radio>
                <Radio value="desktop_only">
                  <DesktopOutlined style={{ marginRight: '4px' }} />
                  Desktop Only
                </Radio>
              </Radio.Group>
            </div>

            {(deviceType === 'all' || deviceType === 'mobile_only') && (
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Operating System (Mobile)
                </label>
                <Radio.Group value={operatingSystem} onChange={(e) => setOperatingSystem(e.target.value)}>
                  <Radio value="both">Both iOS & Android</Radio>
                  <Radio value="ios">iOS Only</Radio>
                  <Radio value="android">Android Only</Radio>
                </Radio.Group>
              </div>
            )}
          </div>

          </div>{/* end placements + device grid */}
        </>
      )}

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid var(--paper-300)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: Placements
        </div>
        <button
          onClick={handleContinue}
          style={{
            padding: '12px 32px',
            background: (placementType === 'advantage' || selectedPlatforms.length > 0) ? 'var(--ink-200)' : 'var(--paper-300)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: (placementType === 'advantage' || selectedPlatforms.length > 0) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (placementType === 'advantage' || selectedPlatforms.length > 0) {
              e.currentTarget.style.background = '#4b5563';
            }
          }}
          onMouseLeave={(e) => {
            if (placementType === 'advantage' || selectedPlatforms.length > 0) {
              e.currentTarget.style.background = 'var(--ink-200)';
            }
          }}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default MetaPlacements;
